import moment from "moment";
import TagManager, { DataLayerArgs, TagManagerArgs } from "react-gtm-module";
import { Cart, ProductDistributionType, Service } from "rsl-api-106/lib/types";

import { getLocalStorageItem } from "@utils/localStorage";

import { Funnel_Type } from "../../types";

//https://septeogroup-my.sharepoint.com/:x:/g/personal/marlene_granet_septeo_com/EZ8rKenmaSNEm6OqM1xc1ZYBO6WZ-iRb8_oKZrnwKwhQMg?wdOrigin=TEAMS-MAGLEV.p2p_ns.rwc&wdExp=TEAMS-TREATMENT&wdhostclicktime=1732177935985&web=1

declare global {
  interface Window {
    dataLayer: Record<string, any>[];
  }
}

export const initGTM = (gtmId: string, dataLayer?: DataLayerArgs) => {
  const tagManagerArgs: TagManagerArgs = {
    gtmId: gtmId,
    dataLayer: dataLayer,
  };
  TagManager.initialize(tagManagerArgs);
};

export const sendDataLayer = (payload: object) => {
  const localAuthMode = getLocalStorageItem("authMode");
  const authMode =
    localAuthMode && !isNaN(parseInt(localAuthMode))
      ? parseInt(localAuthMode)
      : 0;
  if (authMode !== 2) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }
};

export const trackClick = (
  category: string,
  action: string,
  label: string,
  value: string,
  event?: string
) => {
  sendDataLayer({
    eventCategory: category,
    eventAction: action,
    eventLabel: label,
    eventValue: value,
    event: event ? event : "Event clic",
  });
};

const getItems = (cart: Cart | null) => {
  let items: any = [];
  for (let reservation of cart?.reservations ?? []) {
    for (let stay of reservation?.stays ?? []) {
      items = [
        ...items,
        {
          price: stay?.totalAmount?.amount,
          currency: reservation?.totalAmount?.currencyCode,
          quantity: 1,
          item_id: reservation?.property?.id,
          item_name: reservation?.property?.label,
          item_brand: "vacanceole",
          item_variant: stay?.occupants?.[0]?.lineItems?.find(
            (x) => x?.product?.isBase
          )?.product?.code,
          item_category: stay?.distribution?.roomTypes
            ?.map((x) => x?.code)
            .join(","),
          item_category2: reservation?.property?.label,
          duree_sejour: stay?.nbDays,
          date_arrivee: moment(stay?.startDate, "YYYY-MM-DD").format(
            "DDMMYYYY"
          ),
          date_depart: moment(stay?.endDate, "YYYY-MM-DD").format("DDMMYYYY"),
          participant: stay?.occupants?.length ?? 1,
        },
      ];
    }
  }
  return items;
};

const getItemsWithPrestations = (cart: Cart | null) => {
  let items: any = [];
  for (let reservation of cart?.reservations ?? []) {
    for (let stay of reservation?.stays ?? []) {
      const prestation_hebergement: string[] = [];
      for (let occupant of stay?.occupants ?? []) {
        for (let lineItems of occupant?.lineItems ?? []) {
          if (
            (lineItems?.product?.distributionType ===
              ProductDistributionType.Room ||
              lineItems?.product?.distributionType ===
                ProductDistributionType.Global) &&
            !lineItems?.product?.isBase
          ) {
            prestation_hebergement.push(lineItems?.product?.label ?? "");
          }
        }
      }

      const prestation_occupant: string[] = [];
      for (let occupant of stay?.occupants ?? []) {
        for (let lineItems of occupant?.lineItems ?? []) {
          if (
            lineItems?.product?.distributionType ===
              ProductDistributionType.Occupant &&
            !lineItems?.product?.isBase
          ) {
            prestation_occupant.push(lineItems?.product?.label ?? "");
          }
        }
      }

      items = [
        ...items,
        {
          price: stay?.totalAmount?.amount,
          currency: reservation?.totalAmount?.currencyCode,
          quantity: 1,
          item_id: reservation?.property?.id,
          item_name: reservation?.property?.label,
          item_brand: "vacanceole",
          item_variant: stay?.occupants?.[0]?.lineItems?.find(
            (x) => x?.product?.isBase
          )?.product?.code,
          item_category: stay?.distribution?.roomTypes
            ?.map((x) => x?.code)
            .join(","),
          item_category2: reservation?.property?.label,
          prestation_hebergement: JSON.stringify(prestation_hebergement),
          prestation_occupant: JSON.stringify(prestation_occupant),
          duree_sejour: stay?.nbDays,
          date_arrivee: moment(stay?.startDate, "YYYY-MM-DD").format(
            "DDMMYYYY"
          ),
          date_depart: moment(stay?.endDate, "YYYY-MM-DD").format("DDMMYYYY"),
          participant: stay?.occupants?.length ?? 1,
        },
      ];
    }
  }
  return items;
};

const getItemsWithPrestationsAndInsurance = (cart: Cart | null) => {
  let items: any = [];
  for (let reservation of cart?.reservations ?? []) {
    for (let stay of reservation?.stays ?? []) {
      const prestation_hebergement: string[] = [];
      const prestation_occupant: string[] = [];
      for (let occupant of stay?.occupants ?? []) {
        for (let lineItems of occupant?.lineItems ?? []) {
          if (
            lineItems?.product?.distributionType ===
              ProductDistributionType.Occupant &&
            !lineItems?.product?.isBase
          ) {
            prestation_occupant.push(lineItems?.product?.label ?? "");
          }
          if (
            (lineItems?.product?.distributionType ===
              ProductDistributionType.Room ||
              lineItems?.product?.distributionType ===
                ProductDistributionType.Global) &&
            !lineItems?.product?.isBase
          ) {
            prestation_hebergement.push(lineItems?.product?.label ?? "");
          }
        }
      }

      let insurance = "";
      for (let occupant of stay?.occupants ?? []) {
        for (let lineItems of occupant?.lineItems ?? []) {
          if (lineItems?.product?.categoryCode === "ASSUR") {
            insurance = lineItems?.product?.label ?? "";
          }
        }
      }

      items = [
        ...items,
        {
          price: stay?.totalAmount?.amount,
          currency: reservation?.totalAmount?.currencyCode,
          quantity: 1,
          item_id: reservation?.property?.id,
          item_name: reservation?.property?.label,
          item_brand: "vacanceole",
          item_variant: stay?.occupants?.[0]?.lineItems?.find(
            (x) => x?.product?.isBase
          )?.product?.code,
          item_category: stay?.distribution?.roomTypes
            ?.map((x) => x?.code)
            .join(","),
          item_category2: reservation?.property?.label,
          prestation_hebergement: JSON.stringify(prestation_hebergement),
          prestation_occupant: JSON.stringify(prestation_occupant),
          insurance: insurance,
          duree_sejour: stay?.nbDays,
          date_arrivee: moment(stay?.startDate, "YYYY-MM-DD").format(
            "DDMMYYYY"
          ),
          date_depart: moment(stay?.endDate, "YYYY-MM-DD").format("DDMMYYYY"),
          participant: stay?.occupants?.length ?? 1,
        },
      ];
    }
  }
  return items;
};

//4. L'utilisateur ajoute un produit au panier (chooseProposalFromCart success)
export const sendDataLayerAddToCart = (cart: Cart | null) => {
  sendDataLayer({ ecommerce: null });
  sendDataLayer({
    event: "add_to_cart",
    ecommerce: {
      value: cart?.reservations?.[0]?.totalAmount?.amount,
      items: getItems(cart),
    },
  });
};

//6. L'utilisateur arrive sur la page panier / étape d'authentification
export const sendDataLayerViewCart = (cart: Cart | null) => {
  sendDataLayer({ ecommerce: null });
  sendDataLayer({
    event: "view_cart",
    ecommerce: {
      value: cart?.reservations?.[0]?.totalAmount?.amount,
      items: getItems(cart),
    },
  });
};

//7. L'utilisateur se connecte à son compte avec succès
export const sendDataLayerLogin = (
  cart: Cart | null,
  funnel_type: Funnel_Type
) => {
  sendDataLayer({ ecommerce: null });
  sendDataLayer({
    event: "checkout_connexion",
    funnel_type: funnel_type,
    ecommerce: {
      value: cart?.reservations?.[0]?.totalAmount?.amount,
      items: getItems(cart),
    },
  });
};

//7.2 - Todo: L'utilisateur s'inscrit avec succès
export const sendDataLayerInscription = (
  cart: Cart | null,
  funnel_type: Funnel_Type,
  service: Service | null
) => {
  sendDataLayer({ ecommerce: null });
  sendDataLayer({
    event: "checkout_inscription",
    funnel_type: funnel_type,
    client_email: service?.address?.mail,
    client_birthdate: service?.birthDate,
    client_country: service?.address?.addressCountry?.code,
    ecommerce: {
      value: cart?.reservations?.[0]?.totalAmount?.amount,
      items: getItems(cart),
    },
  });
};

//8. Checkout - l’utilisateur arrive sur la première étape du checkout où il doit choisir les prestations additionnelles
export const sendDataLayerBeginCheckout = (
  cart: Cart | null,
  funnel_type: Funnel_Type
) => {
  sendDataLayer({ ecommerce: null });
  sendDataLayer({
    event: "begin_checkout",
    funnel_type: funnel_type,
    ecommerce: {
      value: cart?.reservations?.[0]?.totalAmount?.amount,
      items: getItems(cart),
    },
  });
};

//9. Checkout - l’utilisateur arrive sur la deuxième étape du checkout où il doit choisir les activités additionnelles
export const sendDataLayerCheckoutActivities = (
  cart: Cart | null,
  funnel_type: Funnel_Type
) => {
  sendDataLayer({ ecommerce: null });
  sendDataLayer({
    event: "checkout_activities",
    funnel_type: funnel_type,
    ecommerce: {
      value: cart?.reservations?.[0]?.totalAmount?.amount,
      items: getItems(cart),
    },
  });
};

//10. Checkout - l'utilisateur ajoute l'assurance optionnelle et renseigne ses informations de paiement
export const sendDataLayerAddPaymentInfo = (
  cart: Cart | null,
  funnel_type: Funnel_Type
) => {
  sendDataLayer({ ecommerce: null });
  sendDataLayer({
    event: "add_payment_info",
    funnel_type: funnel_type,
    ecommerce: {
      value: cart?.reservations?.[0]?.totalAmount?.amount,
      currency: cart?.reservations?.[0]?.totalAmount?.currencyCode,
      //tax: "N/A",
      items: getItemsWithPrestations(cart),
    },
  });
};

//11. L'utilisateur réalise un achat avec succès
export const sendDataLayerPurchase = (
  cart: Cart | null,
  funnel_type: Funnel_Type
) => {
  sendDataLayer({ ecommerce: null });
  sendDataLayer({
    event: "purchase",
    funnel_type: funnel_type,
    ecommerce: {
      transaction_id: cart?.reservations?.[0]?.id,
      value: cart?.reservations?.[0]?.totalAmount?.amount,
      currency: cart?.reservations?.[0]?.totalAmount?.currencyCode,
      coupon: cart?.reservations?.[0]?.discountCode,
      discount: getTotalDiscountOfDiscountCode(cart),
      code_fidelite: getLocalStorageItem("code_fidelite") ?? "",
      //tax: "N/A",
      payment_type: "CB",
      items: getItemsWithPrestationsAndInsurance(cart),
    },
  });
};

const getTotalDiscountOfDiscountCode = (cart: Cart | null) => {
  let total = 0;
  try {
    if (cart && cart?.reservations && cart?.reservations?.length > 0) {
      for (let reservation of cart?.reservations ?? []) {
        for (let stay of reservation?.stays ?? []) {
          for (let occupant of stay?.occupants ?? []) {
            for (let lineItems of occupant?.lineItems ?? []) {
              for (let discount of lineItems?.discounts ?? []) {
                if (
                  discount?.discountCode ===
                  cart?.reservations?.[0]?.discountCode
                ) {
                  total += Math.abs(discount?.price?.amount ?? 0);
                }
              }
            }
          }
        }
      }
    }
  } catch (error) {}

  return total;
};
